import { useState, useEffect } from "react";
import { ArrowUpDown, Filter, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { fetchMentors, fetchUniqueLocations, type MentorData, type SearchParams } from "@/utils/mentorUtils";
import MentorCard from "@/components/MentorCard";

type SortOption = "rating" | "reviews" | "name";

const SearchMentors = () => {
  const [searchParams, setURLSearchParams] = useSearchParams();
  const initialSearchTerm = searchParams.get('q') || '';

  useEffect(() => {
    document.title = "Find Mentors | EMU Mentoring";
  }, []);
  
  const [mentors, setMentors] = useState<MentorData[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [searchParamsState, setSearchParamsState] = useState<SearchParams>({});
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [selectedLocation, setSelectedLocation] = useState<string>("All Locations");
  const [isLoading, setIsLoading] = useState(true);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("rating");
  const [sortedMentors, setSortedMentors] = useState<MentorData[]>([]);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Auto-trigger search if there's an initial search term from URL
  useEffect(() => {
    if (initialSearchTerm) {
      setSearchParamsState({
        searchTerm: initialSearchTerm
      });
    }
  }, [initialSearchTerm]);

  useEffect(() => {
    async function loadLocations() {
      const { data, error, success } = await fetchUniqueLocations();
      if (success && data) {
        setLocations(data);
      } else if (error) {
        console.error("Error loading locations:", error);
      }
    }
    loadLocations();
  }, []);

  useEffect(() => {
    async function loadMentors() {
      setIsLoading(true);
      setError(null);
      
      try {
        
        const { data, error, success } = await fetchMentors(searchParamsState);
        
        if (success && data) {
          
          setMentors(data);
          
          if (data.length > 0 && Object.keys(searchParamsState).length > 0) {
            toast({
              title: "Search Results",
              description: `Found ${data.length} mentors matching your criteria`,
            });
          }
        } else if (error) {
          
          setError(error instanceof Error ? error.message : String(error));
          toast({
            title: "Error",
            description: "Failed to load mentors. Please try again later.",
            variant: "destructive",
          });
        }
      } catch (err) {
        console.error("Exception in loadMentors:", err);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setIsLoading(false);
      }
    }
    
    loadMentors();
  }, [searchParamsState, toast]);

  useEffect(() => {
    const sorted = [...mentors].sort((a, b) => {
      switch (sortBy) {
        case "rating":
          return (b.rating ?? 0) - (a.rating ?? 0);
        case "reviews":
          return (b.reviews ?? 0) - (a.reviews ?? 0);
        case "name":
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
    setSortedMentors(sorted);
  }, [mentors, sortBy]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParamsState({
      ...searchParamsState,
      searchTerm,
      location: selectedLocation !== "All Locations" ? selectedLocation : undefined
    });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedLocation("All Locations");
    setSearchParamsState({});
    setURLSearchParams({});
  };

  const handleViewProfile = (mentorId: string) => {
    console.log(`Navigating to mentor profile: ${mentorId}`);
    navigate(`/mentor-profile/${mentorId}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      <div className="w-full bg-cover bg-center h-64 relative" style={{ backgroundImage: "url('/lovable-uploads/63560c3c-d7c3-4d91-b852-d2cd79229bf8.png')" }}>
        <div className="absolute inset-0 bg-emu-blue/20"></div>
        <div className="container mx-auto px-4 h-full flex items-end pb-8 relative z-10">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">Find Mentors</h1>
            <p className="text-gray-700 mt-2">Connect with experienced professionals in your field</p>
          </div>
        </div>
      </div>
      
      <div className="w-full bg-white pt-10 px-4 md:px-10 pb-10">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gray-50 rounded-xl p-6 shadow-sm border border-gray-100">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="text"
                  placeholder="Search mentors by name, profession, or keywords..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white border border-gray-200 w-full"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap md:flex-nowrap">
              <Select
                value={selectedLocation}
                onValueChange={setSelectedLocation}
              >
                <SelectTrigger className="w-full md:w-[200px] bg-white border border-gray-200">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All Locations">All Locations</SelectItem>
                  {locations.map(location => (
                    <SelectItem key={location} value={location}>{location}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="bg-white text-emu-blue hover:bg-white/90 border-gray-200">
                    <ArrowUpDown className="mr-2 h-4 w-4" />
                    Sort
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setSortBy("rating")}>
                    Sort by Rating
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("reviews")}>
                    Sort by Reviews
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("name")}>
                    Sort by Name
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button type="submit" variant="action" className="bg-emu-blue text-white hover:bg-emu-blue/90">
                Search
              </Button>
              
              {Object.keys(searchParamsState).length > 0 && (
                <Button type="button" variant="ghost" onClick={clearFilters} className="text-gray-700 hover:bg-gray-100">
                  Clear
                </Button>
              )}
            </div>
          </form>
          </div>
        </div>
      </div>
      
      <main className="flex-grow pt-8 pb-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {(searchParamsState.searchTerm || searchParamsState.location) && (
              <div className="mb-6 flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-500">Active filters:</span>
                {searchParamsState.searchTerm && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Search: {searchParamsState.searchTerm}
                  </Badge>
                )}
                {searchParamsState.location && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Location: {searchParamsState.location}
                  </Badge>
                )}
              </div>
            )}
            
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {isLoading ? (
                Array(6).fill(0).map((_, index) => (
                  <Card key={`skeleton-${index}`} className="overflow-hidden">
                    <CardContent className="p-0">
                      <div className="p-6">
                        <div className="flex items-start gap-4">
                          <Skeleton className="w-20 h-20 rounded-full" />
                          <div className="flex-1">
                            <Skeleton className="h-6 w-2/3 mb-2" />
                            <Skeleton className="h-4 w-1/2 mb-1" />
                            <Skeleton className="h-4 w-1/3" />
                          </div>
                        </div>
                        <Skeleton className="h-16 w-full mt-4" />
                        <div className="flex flex-wrap gap-2 mt-3">
                          <Skeleton className="h-5 w-16 rounded-full" />
                          <Skeleton className="h-5 w-20 rounded-full" />
                          <Skeleton className="h-5 w-12 rounded-full" />
                        </div>
                      </div>
                      <div className="flex border-t">
                        <div className="flex-1 p-4 text-center border-r">
                          <Skeleton className="h-4 w-16 mx-auto mb-1" />
                          <Skeleton className="h-6 w-8 mx-auto" />
                        </div>
                        <div className="flex-1 p-4 text-center">
                          <Skeleton className="h-4 w-16 mx-auto mb-1" />
                          <Skeleton className="h-6 w-8 mx-auto" />
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 border-t">
                        <Skeleton className="h-10 w-full" />
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : sortedMentors.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 text-lg">No mentors are currently available with the selected criteria.</p>
                  <p className="text-gray-400 mt-2">Try changing your search or filters.</p>
                  <Button onClick={clearFilters} variant="outline" className="mt-4">
                    Clear Filters
                  </Button>
                  {error && process.env.NODE_ENV === 'development' && (
                    <p className="text-red-500 mt-4 text-sm">Debug error: {error}</p>
                  )}
                </div>
              ) : (
                sortedMentors.map((mentor) => (
                  <MentorCard
                    key={mentor.id}
                    mentor={mentor}
                    onViewProfile={handleViewProfile}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default SearchMentors;
